import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Spinner, Alert, Badge, Form, Row, Col, Button, Pagination, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 20;

const PLAN_OPTIONS = ['', 'Free', 'Basic', 'BasicPlus', 'Pro', 'Pro15', 'Enterprise'];
const CHANGE_PLAN_OPTIONS = PLAN_OPTIONS.filter(Boolean);
const STATUS_OPTIONS = ['', 'active', 'trialing', 'canceled', 'grace', 'inactive', 'past_due'];

const STATUS_LABELS = {
  active:   'Activa',
  trialing: 'Prueba',
  canceled: 'Cancelada',
  grace:    'Gracia',
  inactive: 'Inactiva',
  past_due: 'Vencida',
};

const statusVariant = (status) => {
  if (!status) return 'secondary';
  switch (status.toLowerCase()) {
    case 'active':   return 'success';
    case 'trialing': return 'info';
    case 'grace':    return 'warning';
    case 'past_due': return 'danger';
    case 'canceled': return 'dark';
    case 'inactive': return 'secondary';
    default:         return 'secondary';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

const COLUMNS = [
  { key: 'email',                 label: 'Email' },
  { key: 'planCode',              label: 'Plan' },
  { key: 'subscriptionStatus',    label: 'Estado' },
  { key: 'rfcLimit',              label: 'Límite RFC' },
  { key: 'currentPeriodStartUtc', label: 'Inicio periodo' },
  { key: 'currentPeriodEndUtc',   label: 'Fin periodo' },
  { key: null, label: 'Cancela al final' }, // not sortable
  { key: null, label: 'Acciones' },        // not sortable
];

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuthHeaders();
  const navigate = useNavigate();

  // Extend subscription modal
  const [extendTarget, setExtendTarget] = useState(null); // { userUuid, email }
  const [extendPlanType, setExtendPlanType] = useState('monthly');
  const [extendFromNow, setExtendFromNow] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendFeedback, setExtendFeedback] = useState(null); // { ok, message }

  const openExtendModal = (user) => {
    setExtendTarget(user);
    setExtendPlanType('monthly');
    setExtendFromNow(false);
    setExtendFeedback(null);
  };

  const handleExtend = async () => {
    setExtending(true);
    setExtendFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/admin/users/${extendTarget.userUuid}/extend-subscription`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ planType: extendPlanType, extendFromNow }),
        }
      );
      const data = await response.json();
      setExtendFeedback({ ok: response.ok, message: data.message });
      if (response.ok) fetchUsers(query);
    } catch (err) {
      setExtendFeedback({ ok: false, message: err.message });
    } finally {
      setExtending(false);
    }
  };

  // Change plan modal
  const [changePlanTarget, setChangePlanTarget] = useState(null); // { userUuid, email, planCode }
  const [newPlanCode, setNewPlanCode] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);
  const [changePlanFeedback, setChangePlanFeedback] = useState(null); // { ok, message }

  const openChangePlanModal = (user) => {
    setChangePlanTarget(user);
    setNewPlanCode(user.planCode || '');
    setChangePlanFeedback(null);
  };

  const handleChangePlan = async () => {
    setChangingPlan(true);
    setChangePlanFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/admin/users/${changePlanTarget.userUuid}/change-plan`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ newPlanCode }),
        }
      );
      const data = await response.json();
      setChangePlanFeedback({ ok: response.ok, message: data.message });
      if (response.ok) fetchUsers(query);
    } catch (err) {
      setChangePlanFeedback({ ok: false, message: err.message });
    } finally {
      setChangingPlan(false);
    }
  };

  // Filters (applied on search)
  const [emailInput, setEmailInput] = useState('');
  const [planInput, setPlanInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [userUuidInput, setUserUuidInput] = useState('');

  // Active query sent to API
  const [query, setQuery] = useState({
    email: '', plan: '', status: '', userUuid: '',
    sortBy: 'email', sortDir: 'asc', page: 1, pageSize: PAGE_SIZE,
  });

  // Results
  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce ref for email
  const debounceRef = useRef(null);

  const fetchUsers = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (q.email)       params.set('email', q.email);
      if (q.plan)        params.set('plan', q.plan);
      if (q.status)      params.set('status', q.status);
      if (q.userUuid)    params.set('userUuid', q.userUuid);
      params.set('sortBy',   q.sortBy);
      params.set('sortDir',  q.sortDir);
      params.set('page',     q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/admin/users?${params}`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsers(query);
  }, [query, fetchUsers]);

  // Apply filters button
  const handleSearch = () => {
    setQuery(prev => ({
      ...prev,
      email: emailInput,
      plan: planInput,
      status: statusInput,
      userUuid: userUuidInput,
      page: 1,
    }));
  };

  const handleClear = () => {
    setEmailInput('');
    setPlanInput('');
    setStatusInput('');
    setUserUuidInput('');
    setQuery(prev => ({
      ...prev,
      email: '', plan: '', status: '', userUuid: '',
      page: 1,
    }));
  };

  // Sorting
  const handleSort = (col) => {
    if (!col) return;
    setQuery(prev => ({
      ...prev,
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  // Pagination
  const handlePage = (p) => setQuery(prev => ({ ...prev, page: p }));

  const totalPages = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;

  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) {
      items.push(1);
      if (left > 2) items.push('...');
    }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) items.push('...');
      items.push(totalPages);
    }
    return items;
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Admin — Usuarios</h2>
          <p className="page-subtitle text-muted">
            {result.totalCount} usuario{result.totalCount !== 1 ? 's' : ''} encontrado{result.totalCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={3}>
            <Form.Label className="small mb-1">Email</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Buscar email..."
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Plan</Form.Label>
            <Form.Select size="sm" value={planInput} onChange={e => setPlanInput(e.target.value)}>
              {PLAN_OPTIONS.map(p => (
                <option key={p} value={p}>{p || 'Todos'}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-1">Estado</Form.Label>
            <Form.Select size="sm" value={statusInput} onChange={e => setStatusInput(e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s ? (STATUS_LABELS[s] ?? s) : 'Todos'}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={2}>
            <Form.Label className="small mb-1">User UUID</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Buscar userUuid..."
              value={userUuidInput}
              onChange={e => setUserUuidInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </Col>
          <Col xs={12} md={2} className="d-flex gap-1">
            <Button size="sm" variant="primary" onClick={handleSearch} className="w-100">
              Buscar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleClear} title="Limpiar">
              ✕
            </Button>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">Error al cargar usuarios: {error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  {COLUMNS.map(({ key, label }) => (
                    <th
                      key={label}
                      onClick={() => handleSort(key)}
                      style={{ cursor: key ? 'pointer' : 'default', whiteSpace: 'nowrap', userSelect: 'none' }}
                    >
                      {label}
                      {key && <SortIcon col={key} sortBy={query.sortBy} sortDir={query.sortDir} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((u) => (
                  <tr key={u.userUuid}>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-start"
                        onClick={() => navigate(`/admin/users/${u.userUuid}`)}
                      >
                        {u.email}
                      </Button>
                    </td>
                    <td>{u.planCode ?? '—'}</td>
                    <td>
                      <Badge bg={statusVariant(u.subscriptionStatus)}>
                        {u.subscriptionStatus ? (STATUS_LABELS[u.subscriptionStatus] ?? u.subscriptionStatus) : 'Sin suscripción'}
                      </Badge>
                    </td>
                    <td>{u.rfcLimit}</td>
                    <td>{formatDate(u.currentPeriodStartUtc)}</td>
                    <td>{formatDate(u.currentPeriodEndUtc)}</td>
                    <td>{u.cancelAtPeriodEnd ? 'Sí' : 'No'}</td>
                    <td className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => openExtendModal(u)}
                      >
                        Extender
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => openChangePlanModal(u)}
                      >
                        Cambiar plan
                      </Button>
                    </td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No se encontraron usuarios con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Página {currentPage} de {totalPages} ({result.totalCount} resultados)
              </small>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePage(currentPage - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => handlePage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePage(currentPage + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
      {/* Extend subscription modal */}
      <Modal show={!!extendTarget} onHide={() => !extending && setExtendTarget(null)} centered>
        <Modal.Header closeButton={!extending}>
          <Modal.Title style={{ fontSize: '1rem' }}>Extender suscripción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            <strong>{extendTarget?.email}</strong>
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="small">Tipo de extensión</Form.Label>
            <Form.Select
              size="sm"
              value={extendPlanType}
              onChange={e => setExtendPlanType(e.target.value)}
              disabled={extending}
            >
              <option value="monthly">Mensual (1 mes)</option>
              <option value="yearly">Anual (12 meses)</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small">Calcular desde</Form.Label>
            <Form.Select
              size="sm"
              value={extendFromNow ? '1' : '0'}
              onChange={e => setExtendFromNow(e.target.value === '1')}
              disabled={extending}
            >
              <option value="0">Fecha de fin actual</option>
              <option value="1">Hoy</option>
            </Form.Select>
          </Form.Group>

          {extendFeedback && (
            <Alert variant={extendFeedback.ok ? 'success' : 'danger'} className="mb-0 small py-2">
              {extendFeedback.message}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExtendTarget(null)}
            disabled={extending}
          >
            {extendFeedback?.ok ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!extendFeedback?.ok && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleExtend}
              disabled={extending}
            >
              {extending ? <Spinner animation="border" size="sm" /> : 'Confirmar'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Change plan modal */}
      <Modal show={!!changePlanTarget} onHide={() => !changingPlan && setChangePlanTarget(null)} centered>
        <Modal.Header closeButton={!changingPlan}>
          <Modal.Title style={{ fontSize: '1rem' }}>Cambiar plan de suscripción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            <strong>{changePlanTarget?.email}</strong>
            {' '}— Plan actual: <strong>{changePlanTarget?.planCode ?? '—'}</strong>
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="small">Nuevo plan</Form.Label>
            <Form.Select
              size="sm"
              value={newPlanCode}
              onChange={e => setNewPlanCode(e.target.value)}
              disabled={changingPlan}
            >
              {CHANGE_PLAN_OPTIONS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Alert variant="warning" className="small py-2 mb-0">
            Esto cambia el plan de la suscripción actual del usuario de inmediato (ej. para forzar un downgrade por falta de pago). No modifica las fechas del periodo.
          </Alert>

          {changePlanFeedback && (
            <Alert variant={changePlanFeedback.ok ? 'success' : 'danger'} className="mb-0 mt-3 small py-2">
              {changePlanFeedback.message}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setChangePlanTarget(null)}
            disabled={changingPlan}
          >
            {changePlanFeedback?.ok ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!changePlanFeedback?.ok && (
            <Button
              variant="warning"
              size="sm"
              onClick={handleChangePlan}
              disabled={changingPlan || !newPlanCode || newPlanCode === changePlanTarget?.planCode}
            >
              {changingPlan ? <Spinner animation="border" size="sm" /> : 'Confirmar cambio'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
